import { Stack, Typography, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { convertToMoney } from '@woovi/utils';

import type { PaymentMethodSelectedCustomerRegisterValue_charge$key } from './__generated__/PaymentMethodSelectedCustomerRegisterValue_charge.graphql.ts';
import PaymentMethodBoletoValue from '../active/boleto/PaymentMethodBoletoValue.tsx';
import PaymentMethodCreditCardValue from '../activeWaitingPaymentMethod/creditCard/PaymentMethodCreditCardValue.tsx';
import PaymentMethodPixValue from '../pix/PaymentMethodPixValue.tsx';

type PaymentMethodSelectedCustomerRegisterValueProps = {
  charge: PaymentMethodSelectedCustomerRegisterValue_charge$key;
};

const PaymentMethodSelectedCustomerRegisterValue = (
  props: PaymentMethodSelectedCustomerRegisterValueProps,
): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();

  const charge =
    useFragment<PaymentMethodSelectedCustomerRegisterValue_charge$key>(
      graphql`
        fragment PaymentMethodSelectedCustomerRegisterValue_charge on Charge {
          value
          paymentMethodSelected
          company {
            nameFriendly
          }
          ...PaymentMethodPixValue_charge
          ...PaymentMethodBoletoValue_charge
          ...PaymentMethodCreditCardValue_charge
        }
      `,
      props.charge,
    );

  // TODO: This can be removed, we need to reestructure the payment method root architecture first
  const getMessage = (): ReactNode => {
    if (charge.paymentMethodSelected === PAYMENT_METHOD_TYPE.PIX) {
      return <PaymentMethodPixValue charge={charge} />;
    }

    if (charge.paymentMethodSelected === PAYMENT_METHOD_TYPE.BOLETO) {
      return <PaymentMethodBoletoValue charge={charge} />;
    }

    if (charge.paymentMethodSelected === PAYMENT_METHOD_TYPE.CREDIT_CARD) {
      return <PaymentMethodCreditCardValue charge={charge} />;
    }

    return (
      <Typography variant='h5' fontWeight='bold' textAlign='center'>
        {t('Pay {{value}} to {{companyName}}', {
          value: convertToMoney(charge?.value as number),
          companyName: charge?.company?.nameFriendly,
        })}
      </Typography>
    );
  };

  return (
    <Stack sx={{ gap: 0.5 }}>
      {getMessage()}
      <Typography
        variant='h6'
        textAlign='center'
        sx={{ color: theme.palette.text.secondary }}
      >
        {t('Enter your details to continue')}
      </Typography>
    </Stack>
  );
};

export default PaymentMethodSelectedCustomerRegisterValue;
